import React from 'react';
import { useEditMode } from '@/context/EditModeContext';
import { Pencil, Save, X, Loader2 } from 'lucide-react';

/**
 * Floating admin controls for edit-mode:
 *  - When NOT enabled: shows a small "Edit Page" pill in the bottom-right.
 *  - When enabled: shows a bottom bar with Save / Cancel / dirty indicator.
 * Only visible to admin users.
 */
export const EditToolbar = () => {
  const { enabled, dirty, saving, isAdminUser, enter, cancel, save } = useEditMode();
  if (!isAdminUser) return null;

  if (!enabled) {
    return (
      <button
        type="button"
        onClick={enter}
        className="fixed bottom-6 left-6 z-[60] inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground font-bold shadow-2xl hover:scale-105 transition-transform"
        data-testid="edit-page-btn"
        data-edit-safe
        title="Admin only \u2014 only visible while you are logged in"
      >
        <Pencil className="w-4 h-4" />
        Edit Page
        <span className="ml-1 text-[10px] font-bold uppercase tracking-widest bg-black/25 px-1.5 py-0.5 rounded-full">
          Admin only
        </span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] bg-black/95 backdrop-blur-lg border-t-2 border-yellow-400/70 text-white px-4 py-3 flex items-center justify-between gap-3 shadow-2xl"
      data-testid="edit-toolbar"
      data-edit-safe
    >
      <div className="flex items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1 font-bold text-yellow-300">
          <Pencil className="w-4 h-4" />
          Edit mode
        </span>
        <span className="hidden md:inline text-white/70">
          Click any highlighted text to edit. Press Enter or Tab to confirm.
        </span>
        {dirty && (
          <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-yellow-300 bg-yellow-500/20 px-2 py-0.5 rounded-full">
            Unsaved changes
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={cancel}
          disabled={saving}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold disabled:opacity-50"
          data-testid="edit-cancel-btn"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 disabled:opacity-50"
          data-testid="edit-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export default EditToolbar;
